import { useEffect, useRef, useState } from 'react'
import styles from '../DavidPifView.module.css'
import classNames from 'classnames'
import { Text } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import useHydration from '@/utils/useHydration'
import { DavidButton } from './DavidButton'

export function SpecialMessageSection({ davidEpisodesExperimentEnabled }: { davidEpisodesExperimentEnabled: boolean }) {
  const { t } = useTranslate('pif')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [playerReady, setPlayerReady] = useState(false)
  const { isHydrated } = useHydration()

  const changeChildLifeVideoUrl =
    'https://www.youtube-nocookie.com/embed/bkls-zujgd4?enablejsapi=1&playsinline=1&autoplay=0&mute=1&rel=0&modestbranding=1&controls=1'

  const baseVideoUrl = changeChildLifeVideoUrl
  const [videoSrc, setVideoSrc] = useState<string>(baseVideoUrl)

  useEffect(() => {
    try {
      const origin = window.location.origin
      setVideoSrc(`${baseVideoUrl}&origin=${encodeURIComponent(origin)}`)
    } catch {
      setVideoSrc(baseVideoUrl)
    }
  }, [baseVideoUrl])

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
    const iframe = iframeRef.current
    const container = containerRef.current
    if (!iframe || !container) return

    const send = (func: string, args: unknown[] = []) => {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*')
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!playerReady) return
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
          send('playVideo')
        } else {
          send('pauseVideo')
        }
      },
      { threshold: [0, 0.5] },
    )

    io.observe(container)

    const onVis = () => {
      if (document.hidden) send('pauseVideo')
    }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      io.disconnect()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [playerReady])

  return (
    <section
      className={classNames(
        'relative w-full bg-[#141447]',
        styles['section-lg'],
        davidEpisodesExperimentEnabled && '!pt-12 md:!pt-[4rem] lg:!pt-[6rem]',
      )}
    >
      <div className="relative mx-auto flex w-full max-w-[1440px] shrink-0 flex-col-reverse content-stretch items-start gap-[40px] lg:flex-col lg:gap-[96px]">
        <div className="relative flex w-full max-w-[42.5rem] flex-col content-stretch items-start gap-[2.5rem] self-stretch">
          <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[12px] not-italic">
            <p className="font-angel-royal relative w-full text-5xl lowercase not-italic leading-[2.5rem] text-white md:text-[3.5rem] md:leading-[3rem]">
              {t('davidPhilMessageTitle', 'A Message from Phil Cunningham, Creator of David')}
            </p>
            <Text weight="medium" className="photon-paragraph-md md:photon-paragraph-lg !text-[#C9EDF2]">
              {t(
                'davidPhilMessageDescription',
                'Phil Cunningham, the Creator of DAVID, shares why this story matters—and how you can help send 1 million kids to see it in theaters for free.',
              )}
            </Text>
          </div>

          <div className="relative flex flex-col content-stretch items-start gap-[8px] self-stretch md:shrink-0 md:flex-row">
            <DavidButton variant="cyan" openPifOverlay className="w-full md:w-auto">
              {t('payItForward', 'Pay it Forward')}
            </DavidButton>
            <DavidButton variant="white" href="/tickets/david" className="w-full md:w-auto">
              {t('getShowtimes', 'Get Showtimes')}
            </DavidButton>
          </div>
        </div>
        <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[1rem] md:items-end md:gap-[1.5rem]">
          <div
            ref={containerRef}
            className="aspect-h-9 aspect-w-16 relative w-full overflow-hidden rounded-2xl lg:rounded-3xl"
          >
            {isHydrated ? (
              <iframe
                suppressHydrationWarning
                ref={iframeRef}
                src={videoSrc}
                title="Director Phil Cunningham Special Message"
                className="h-full w-full"
                allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                onLoad={() => setPlayerReady(true)}
              />
            ) : (
              <div className="h-full w-full" />
            )}
          </div>
          <p className="font-angelSans-medium relative shrink-0 whitespace-pre text-nowrap text-right text-[12px] not-italic leading-[16px] text-[#c9edf2]">
            {t('davidPhilMessageCaption', 'A special message from Creator, Phil Cunningham')}
          </p>
        </div>
      </div>
    </section>
  )
}
